import React from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  Badge,
  Button,
  LoadingSkeleton,
  type Column,
} from '@redbax/ui-kit';
import { ArrowLeft, Plus, Trash2, ShieldCheck, ShieldX } from 'lucide-react';
import type { BlocklistEntry, AllowlistEntry, ApprovalRequest } from '@redbax/shared';
import { formatDate } from '@redbax/shared';
import {
  usePackagePolicyByEcosystem,
  useUpdatePackagePolicy,
  useAddToBlocklist,
  useRemoveFromBlocklist,
  useAddToAllowlist,
  useRemoveFromAllowlist,
  useApprovalRequests,
  useApproveRequest,
  useRejectRequest,
} from '../api/package-policy-hooks';
import { PackagePolicyForm } from '../components/package-policy-form';
import { BlocklistEntryForm } from '../components/blocklist-entry-form';
import { ApprovalRequestCard } from '../components/approval-request-card';

const allowlistColumns = (onRemove: (id: string) => void): Column<AllowlistEntry>[] => [
  {
    key: 'packageName',
    header: 'Package',
    sortable: true,
    render: (row) => (
      <span className="block max-w-[180px] truncate" title={row.packageName}>{row.packageName}</span>
    ),
  },
  {
    key: 'versionPattern',
    header: 'Version',
    render: (row) => (
      <span className="text-text-secondary text-xs">{row.versionPattern ?? '*'}</span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Approved',
    render: (row) => (
      <span className="text-text-secondary whitespace-nowrap">
        {formatDate(row.createdAt)}
      </span>
    ),
  },
  {
    key: 'actions',
    header: '',
    render: (row) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(row.id); }}
        className="rounded p-1 text-text-secondary opacity-60 transition-all hover:bg-red-subtle hover:text-red-accent hover:opacity-100"
        aria-label="Remove"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    ),
  },
];

const blocklistColumns = (onRemove: (id: string) => void): Column<BlocklistEntry>[] => [
  {
    key: 'packageName',
    header: 'Package',
    sortable: true,
    render: (row) => (
      <span className="block max-w-[180px] truncate" title={row.packageName}>{row.packageName}</span>
    ),
  },
  {
    key: 'versionPattern',
    header: 'Version',
    render: (row) => (
      <span className="text-text-secondary text-xs">{row.versionPattern ?? '*'}</span>
    ),
  },
  {
    key: 'reason',
    header: 'Reason',
    render: (row) => (
      <span className="text-text-secondary text-xs block max-w-[120px] truncate" title={row.reason ?? ''}>{row.reason ?? '—'}</span>
    ),
  },
  {
    key: 'actions',
    header: '',
    render: (row) => (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(row.id); }}
        className="rounded p-1 text-text-secondary opacity-60 transition-all hover:bg-red-subtle hover:text-red-accent hover:opacity-100"
        aria-label="Remove"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    ),
  },
];

function AllowlistAddForm({
  open,
  onOpenChange,
  ecosystem,
  onSubmit,
  isSubmitting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ecosystem: string;
  onSubmit: (data: {
    ecosystem: string;
    packageName: string;
    versionPattern?: string | null;
    expiresAt?: string | null;
  }) => void;
  isSubmitting: boolean;
}) {
  const [packageName, setPackageName] = React.useState('');
  const [versionPattern, setVersionPattern] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState('');

  if (!open) return null;

  const handleSubmit = () => {
    if (!packageName.trim()) return;
    onSubmit({
      ecosystem,
      packageName: packageName.trim(),
      versionPattern: versionPattern.trim() || null,
      expiresAt: expiresAt || null,
    });
    setPackageName('');
    setVersionPattern('');
    setExpiresAt('');
    onOpenChange(false);
  };

  return (
    <div className="border-t border-border-default px-4 py-3 space-y-2">
      <input
        placeholder="Package name"
        value={packageName}
        onChange={(e) => setPackageName(e.target.value)}
        className="w-full rounded border border-border-default bg-background px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-red-accent"
      />
      <div className="flex gap-2">
        <input
          placeholder="Version"
          value={versionPattern}
          onChange={(e) => setVersionPattern(e.target.value)}
          className="flex-1 rounded border border-border-default bg-background px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-red-accent"
        />
        <input
          type="date"
          placeholder="Expires"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          className="flex-1 rounded border border-border-default bg-background px-2.5 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-red-accent"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} className="h-7 px-2.5 text-xs">
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={!packageName.trim() || isSubmitting} className="h-7 px-2.5 text-xs">
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </div>
    </div>
  );
}

export function PackagePolicyDetailPage() {
  const { ecosystem } = useParams<{ ecosystem: string }>();
  const navigate = useNavigate();
  const decodedEcosystem = ecosystem ? decodeURIComponent(ecosystem) : '';

  const { data: policy, isLoading } = usePackagePolicyByEcosystem(decodedEcosystem);
  const updatePolicy = useUpdatePackagePolicy(decodedEcosystem);
  const addToBlocklist = useAddToBlocklist(decodedEcosystem);
  const removeFromBlocklist = useRemoveFromBlocklist(decodedEcosystem);
  const addToAllowlist = useAddToAllowlist(decodedEcosystem);
  const removeFromAllowlist = useRemoveFromAllowlist(decodedEcosystem);
  const { data: approvalRequests } = useApprovalRequests(decodedEcosystem);
  const approveRequest = useApproveRequest();
  const rejectRequest = useRejectRequest();

  const pendingRequests = React.useMemo(
    () => approvalRequests?.filter((r) => r.status === 'Pending') ?? [],
    [approvalRequests],
  );

  const [showBlocklistForm, setShowBlocklistForm] = React.useState(false);
  const [showAllowlistForm, setShowAllowlistForm] = React.useState(false);
  const [processingRequestId, setProcessingRequestId] = React.useState<string | null>(null);

  const handleAllowRequest = (request: ApprovalRequest) => {
    setProcessingRequestId(request.id);
    addToAllowlist.mutate(
      {
        ecosystem: decodedEcosystem,
        packageName: request.packageName,
        versionPattern: request.packageVersion,
      },
      {
        onSuccess: () => {
          approveRequest.mutate({ id: request.id, note: 'Added to allowlist' },
            { onSettled: () => setProcessingRequestId(null) });
        },
        onError: () => {
          approveRequest.mutate({ id: request.id, note: 'Added to allowlist' },
            { onSettled: () => setProcessingRequestId(null) });
        },
      },
    );
  };

  const handleBlockRequest = (request: ApprovalRequest) => {
    setProcessingRequestId(request.id);
    addToBlocklist.mutate(
      {
        ecosystem: decodedEcosystem,
        packageName: request.packageName,
        versionPattern: request.packageVersion,
        reason: 'Rejected via approval request',
      },
      {
        onSuccess: () => {
          rejectRequest.mutate({ id: request.id, note: 'Added to blocklist' },
            { onSettled: () => setProcessingRequestId(null) });
        },
        onError: () => {
          rejectRequest.mutate({ id: request.id, note: 'Added to blocklist' },
            { onSettled: () => setProcessingRequestId(null) });
        },
      },
    );
  };

  if (!ecosystem) {
    return null;
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title={`${decodedEcosystem} Policy`} description="Loading policy..." />
        <LoadingSkeleton rows={6} />
      </div>
    );
  }

  if (!policy) {
    return (
      <div>
        <PageHeader title={`${decodedEcosystem} Policy`} description="Policy not found" />
        <div className="flex h-32 items-center justify-center text-sm text-text-secondary">
          No policy found for ecosystem &quot;{decodedEcosystem}&quot;.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`${decodedEcosystem} Policy`}
        description={`Configure package security settings for ${decodedEcosystem}`}
        actions={
          <Button variant="outline" onClick={() => navigate('/package-security')}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Policies
          </Button>
        }
      />

      {pendingRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">
              Pending Approval Requests ({pendingRequests.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingRequests.map((request) => (
              <ApprovalRequestCard
                key={request.id}
                request={request}
                onAddToAllowlist={handleAllowRequest}
                onAddToBlocklist={handleBlockRequest}
                isProcessing={processingRequestId === request.id}
              />
            ))}
          </CardContent>
        </Card>
      )}

      <PackagePolicyForm
        policy={policy}
        onSave={(data) => updatePolicy.mutate(data)}
        isSaving={updatePolicy.isPending}
      />

      {/* Allowlist & Blocklist — side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Allowlist */}
        <Card className="flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-green-ok" />
              <CardTitle className="text-sm">Allowlist</CardTitle>
              {policy.allowlistEntries.length > 0 && (
                <span className="text-xs text-text-secondary">({policy.allowlistEntries.length})</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowAllowlistForm(true)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-surface-raised hover:text-text-primary"
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
          </CardHeader>
          <CardContent className="flex-1 pt-0">
            <DataTable
              columns={allowlistColumns((id) => removeFromAllowlist.mutate(id))}
              data={policy.allowlistEntries}
              keyExtractor={(row) => row.id}
              emptyMessage="No allowed packages"
              compact
            />
            <AllowlistAddForm
              open={showAllowlistForm}
              onOpenChange={setShowAllowlistForm}
              ecosystem={decodedEcosystem}
              onSubmit={(data) => addToAllowlist.mutate(data)}
              isSubmitting={addToAllowlist.isPending}
            />
          </CardContent>
        </Card>

        {/* Blocklist */}
        <Card className="flex flex-col">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <ShieldX className="h-4 w-4 text-red-accent" />
              <CardTitle className="text-sm">Blocklist</CardTitle>
              {policy.blocklistEntries.length > 0 && (
                <span className="text-xs text-text-secondary">({policy.blocklistEntries.length})</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowBlocklistForm(true)}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-text-secondary transition-colors hover:bg-surface-raised hover:text-text-primary"
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
          </CardHeader>
          <CardContent className="flex-1 pt-0">
            <DataTable
              columns={blocklistColumns((id) => removeFromBlocklist.mutate(id))}
              data={policy.blocklistEntries}
              keyExtractor={(row) => row.id}
              emptyMessage="No blocked packages"
              compact
            />
          </CardContent>
        </Card>
      </div>

      <BlocklistEntryForm
        open={showBlocklistForm}
        onOpenChange={setShowBlocklistForm}
        onSubmit={(data) => addToBlocklist.mutate({ ...data, ecosystem: decodedEcosystem })}
        isSubmitting={addToBlocklist.isPending}
        fixedEcosystem={decodedEcosystem}
      />

    </div>
  );
}
